// DiagiaCampaignDetail — one campaign, with header, KPI strip, recent
// diagnostics within it, and the invited participants list.

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaArrowRight,
  FaEnvelope,
  FaUsers,
  FaCheckDouble,
  FaPercentage,
  FaTrash,
  FaSpinner,
  FaCopy,
  FaCheck,
  FaPlayCircle,
  FaCheckCircle,
  FaPlus,
} from 'react-icons/fa';

import useDiagiaApi from '../hooks/useDiagiaApi';
import KpiCard from '../components/KpiCard';

const formatPct = (p) => {
  if (p === null || p === undefined) return '—';
  return `${Math.round(p * 100)}%`;
};

export default function DiagiaCampaignDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useDiagiaApi();

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [copiedToken, setCopiedToken] = useState('');
  const [addingEmails, setAddingEmails] = useState('');
  const [adding, setAdding] = useState(false);

  const PARTICIPANT_STATUS = {
    invited:   { label: 'Invité',   className: 'is-draft',  icon: <FaEnvelope /> },
    started:   { label: 'Commencé', className: 'is-draft',  icon: <FaPlayCircle /> },
    completed: { label: 'Complété', className: 'is-active', icon: <FaCheckCircle /> },
  };

  const buildInviteUrl = (token) => `${window.location.origin}/invite/${token}`;

  const copyToken = async (token) => {
    try {
      await navigator.clipboard.writeText(buildInviteUrl(token));
      setCopiedToken(token);
      setTimeout(() => setCopiedToken((t) => (t === token ? '' : t)), 1800);
    } catch (e) { /* noop */ }
  };

  const handleAddParticipants = async (e) => {
    e.preventDefault();
    const raw = addingEmails.trim();
    if (!raw) return;
    const emails = raw.split(/[\s,;]+/).filter(Boolean);
    setAdding(true);
    try {
      await api.addCampaignParticipants(id, emails);
      setAddingEmails('');
      await load();
    } catch (err) {
      window.alert(err?.response?.data?.error || 'Échec de l\'ajout');
    } finally {
      setAdding(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.getCampaign(id);
      setDoc(data);
    } catch (err) {
      setError(err?.response?.status === 404
        ? 'Campagne introuvable'
        : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [api, id]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!doc) return;
    const confirmed = window.confirm(
      `Supprimer définitivement la campagne « ${doc.name} » ?\n\n` +
      `Les diagnostics qu'elle contient ne seront pas supprimés, ils ne seront simplement plus rattachés.`,
    );
    if (!confirmed) return;
    setDeleting(true);
    try {
      await api.deleteCampaign(id);
      navigate('/diagia/campaigns', { replace: true });
    } catch (e) {
      window.alert(e?.response?.data?.error || 'Suppression impossible');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="dia-loading">
        <FaSpinner className="dia-spin" /> Chargement…
      </div>
    );
  }
  if (error || !doc) {
    return (
      <div className="dia-page">
        <p className="dia-error">{error || 'Campagne introuvable'}</p>
        <Link to="/diagia/campaigns" className="dia-cta dia-cta-ghost">
          <FaArrowLeft /> Retour aux campagnes
        </Link>
      </div>
    );
  }

  const progressionPct = doc.progression !== null && doc.progression !== undefined
    ? Math.min(100, Math.round(doc.progression * 100))
    : 0;

  return (
    <div className="dia-page">
      <header className="dia-page-head">
        <div className="dia-page-head-meta">
          <button
            type="button"
            className="dia-icon-btn"
            onClick={() => navigate('/diagia/campaigns')}
            aria-label="Retour"
          >
            <FaArrowLeft />
          </button>
          <div>
            <span className="dia-section-eyebrow">{t('diagia.part.th_campaign', 'Campagne')}</span>
            <h1>{doc.name}</h1>
            <p>
              {doc.scope && <span>{doc.scope} · </span>}
              Créée le {new Date(doc.created_at).toLocaleDateString('fr-FR')}
            </p>
          </div>
        </div>
        <div className="dia-head-actions">
          <Link
            to={`/diagia/new?campaign=${doc.id}`}
            className="dia-cta dia-cta-primary"
          >
            Lancer un diagnostic <FaArrowRight />
          </Link>
          <button
            type="button"
            className="dia-cta dia-cta-ghost dia-cta-danger"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? <FaSpinner className="dia-spin" /> : <FaTrash />}
            Supprimer
          </button>
        </div>
      </header>

      {doc.description && (
        <div className="dia-card dia-card-tight">
          <p style={{ margin: 0 }}>{doc.description}</p>
        </div>
      )}

      <div className="dia-kpis">
        <KpiCard
          icon={<FaUsers />}
          tone="mist"
          label="Participants invités"
          value={doc.participant_count ?? 0}
        />
        <KpiCard
          icon={<FaCheckDouble />}
          tone="amber"
          label="Diagnostics complétés"
          value={doc.diagnostics_count ?? 0}
        />
        <KpiCard
          icon={<FaPercentage />}
          tone="sky"
          label="Taux de complétion"
          value={formatPct(doc.progression)}
        />
        <KpiCard
          icon={<FaEnvelope />}
          tone="lilac"
          label="Statut"
          value={doc.status === 'active' ? 'Active' : doc.status === 'archived' ? 'Archivée' : 'Brouillon'}
        />
      </div>

      <div className="dia-card">
        <header className="dia-card-head">
          <h3>{t('diagia.detail.diags', 'Diagnostics de la campagne')}</h3>
          <span className="dia-card-sub">
            {progressionPct}% de progression
          </span>
        </header>

        {(doc.diagnostics || []).length === 0 && (
          <p className="dia-empty">
            Aucun diagnostic n'a encore été soumis dans cette campagne.
          </p>
        )}

        {(doc.diagnostics || []).length > 0 && (
          <table className="dia-table">
            <thead>
              <tr>
                <th>{t('diagia.home.th_title', 'Titre')}</th>
                <th>{t('diagia.home.th_score', 'Score')}</th>
                <th>{t('diagia.home.th_lang', 'Langue')}</th>
                <th>{t('diagia.home.th_created', 'Créé le')}</th>
              </tr>
            </thead>
            <tbody>
              {doc.diagnostics.map((d) => (
                <tr key={d.id}>
                  <td className="dia-table-title">
                    <Link to={`/diagia/diagnostics/${d.id}`}>{d.title || 'Sans titre'}</Link>
                  </td>
                  <td>
                    {typeof d.score === 'number' ? (
                      <span className="dia-score-pill">{d.score}/100</span>
                    ) : (
                      <span className="dia-table-muted">—</span>
                    )}
                  </td>
                  <td>{(d.language || '').toUpperCase() || '—'}</td>
                  <td>{new Date(d.created_at).toLocaleDateString('fr-FR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="dia-card">
        <header className="dia-card-head">
          <h3>{t('diagia.detail.invited', 'Participants invités')}</h3>
          <span className="dia-card-sub">
            Partagez le lien personnel à chaque invité — chaque token est unique.
          </span>
        </header>

        {(doc.participants || []).length === 0 && (
          <p className="dia-empty">{t('diagia.detail.none_invited', "Personne n'a encore été invité.")}</p>
        )}

        {(doc.participants || []).length > 0 && (
          <table className="dia-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>{t('diagia.home.th_status', 'Statut')}</th>
                <th>Date</th>
                <th>{t('diagia.part.th_link', "Lien d'invitation")}</th>
              </tr>
            </thead>
            <tbody>
              {doc.participants.map((p) => {
                const meta = PARTICIPANT_STATUS[p.status] || PARTICIPANT_STATUS.invited;
                const dateRaw = p.completed_at || p.started_at || p.invited_at;
                return (
                  <tr key={p.token}>
                    <td className="dia-table-title">{p.email}</td>
                    <td>
                      <span className={`dia-status ${meta.className}`}>
                        {meta.icon} {meta.label}
                      </span>
                    </td>
                    <td>{dateRaw ? new Date(dateRaw).toLocaleDateString('fr-FR') : '—'}</td>
                    <td>
                      {p.status === 'completed' && p.diagnostic_id ? (
                        <Link
                          to={`/diagia/diagnostics/${p.diagnostic_id}`}
                          className="dia-card-link"
                        >
                          Voir le diagnostic →
                        </Link>
                      ) : (
                        <button
                          type="button"
                          className="dia-cta dia-cta-ghost dia-cta-mini"
                          onClick={() => copyToken(p.token)}
                          title={t('diagia.part.copy_link', "Copier le lien d'invitation")}
                        >
                          {copiedToken === p.token ? <FaCheck /> : <FaCopy />}
                          {copiedToken === p.token ? 'Copié' : 'Copier le lien'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <form className="dia-add-participants" onSubmit={handleAddParticipants}>
          <input
            type="text"
            value={addingEmails}
            onChange={(e) => setAddingEmails(e.target.value)}
            placeholder={t('diagia.detail.add_invites_ph', "Ajouter d'autres invités — séparez par virgule ou espace")}
            disabled={adding}
          />
          <button
            type="submit"
            className="dia-cta dia-cta-primary dia-cta-mini"
            disabled={adding || !addingEmails.trim()}
          >
            {adding ? <FaSpinner className="dia-spin" /> : <FaPlus />}
            Ajouter
          </button>
        </form>
      </div>
    </div>
  );
}
